/**
 * Authentication Service
 * Handles user login, logout, token management, and authentication state
 * Migrated from frontend/js/auth.js with TypeScript and improved architecture
 */

import configService from '@services/config';
import type { User, LoginRequest, LoginResponse, TenantDefaultUserSettings } from '@/types/models';

/**
 * One option in the "pick which tenant to log in to" picker shown by
 * LoginPage when the URL is a company with `multi_user_login_allowed=true`
 * AND the email matches more than one sub-tenant user account.
 */
export interface TenantPickerOption {
  tenant_id: string;
  tenant_code: string | null;
  tenant_name: string | null;
  user_role: string;
}
// Direct import from the leaf module (NOT the routing barrel) — the
// barrel re-exports BrandResolver which depends on api.ts which
// depends on this file, producing a circular import that leaves
// `isReservedSlug` undefined when authService is loaded first.
import { isReservedSlug } from '@/routing/reservedSlugs';
import { DEFAULT_LOGIN_PATH } from '@/routing/paths';

const AUTH_KEYS = {
  ACCESS_TOKEN: 'access_token',
  USER: 'user',
  TENANT_ID: 'tenant_id',
  LOGIN_TIME: 'login_time',
  /** When this tab's access token expires (ISO) — from the server's
   *  `expires_in`, which a remembered sign-in can shorten near its end. */
  EXPIRES_AT: 'session_expires_at',
  AUTH_TYPE: 'auth_type',
  LANGUAGE: 'language',
  THEME: 'theme',
  /** The TENANT's defaults, not the user's choice. Kept under its own key
   *  precisely so the two stay distinguishable: an absent LANGUAGE/THEME
   *  means "this person has never decided", which is the only condition
   *  under which a tenant default is allowed to apply. Merging them would
   *  make the first page load look like a decision. */
  TENANT_DEFAULTS: 'tenant_default_user_settings',
  /** "Remember me" — localStorage, so they outlive the tab and the browser.
   *  The email prefills the login form; the session holds the single-use
   *  refresh token a new tab trades for an access token. The password is
   *  never stored by the platform — that is the browser's password manager. */
  REMEMBERED_EMAIL: 'remembered_email',
  REMEMBERED_SESSION: 'remembered_session',
} as const;

/**
 * The remembered sign-in held by this browser. One per origin: the latest
 * "Remember me" sign-in replaces the previous one (which is revoked).
 */
interface RememberedSession {
  refresh_token: string;
  user_id: string;
  /** The login surface the sign-in belongs to (company slug, else tenant
   *  slug, '' for a slug-less super admin). A tab on ANOTHER tenant's URL is
   *  never restored into this session — it shows that tenant's login. */
  login_slug: string;
  /** ISO — when the server ends the family regardless of use. */
  expires_at: string;
}

/** Name of the Web Lock that serialises refresh exchanges across tabs. A
 *  refresh token is single-use and presenting a used one again signs the
 *  person out everywhere, so two tabs restoring at once must take turns — the
 *  second one reads the first one's successor token. */
const REFRESH_LOCK = 'corpbrain-remembered-session-refresh';

/** An access token's lifetime when the server did not say (it always does). */
const SESSION_HOURS = 48;

/** How long a logout waits for the server to confirm before leaving the page.
 *  The navigation that follows a logout would otherwise cancel the request
 *  before the session is ended. */
const LOGOUT_WAIT_MS = 3000;

/**
 * Auth-bearing keys (ACCESS_TOKEN, USER, TENANT_ID, LOGIN_TIME,
 * AUTH_TYPE) live in **sessionStorage**, not localStorage. Rationale:
 * sessionStorage is per-tab, so two browser tabs can simultaneously
 * hold sessions for DIFFERENT tenants — pasting `/tenant-b/login` in
 * a new tab while you're already authenticated to tenant-a in
 * another tab no longer silently re-auths you as user-a; the new tab
 * shows tenant-b's login form, and a successful login there leaves
 * the original tab's tenant-a session untouched.
 *
 * Preference keys (LANGUAGE, THEME) intentionally stay in
 * localStorage so they survive tab close and apply globally.
 */
const AUTH_STORE: Storage = typeof window !== 'undefined'
  ? window.sessionStorage
  : ({
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
      clear: () => {},
      key: () => null,
      length: 0,
    } as Storage);
export { AUTH_STORE as authSessionStore };

/**
 * Allowlist of storage keys/prefixes that are SAFE to keep across a
 * logout-and-login-as-different-user cycle. Anything not on this list
 * is treated as user-scoped data and wiped on logout AND on the next
 * login. The allowlist approach (vs. an explicit denylist) means any
 * new cache added by future code automatically gets wiped — privacy
 * by default, no forgotten leaks.
 *
 * What's safe to keep:
 *   - language / theme — UI preferences, not user content.
 *   - brand_overrides_v5:tenant:* and brand_page_title:* — per-TENANT
 *     brand cache; two users in the same tenant share the same brand
 *     and we don't want to flash the default brand on login.
 *   - vanity_host_slug:* — which tenant a custom DOMAIN maps to. A
 *     host-level fact (not user-scoped); wiping it would force a
 *     re-resolve flash — or, on a host that can't re-resolve, drop the
 *     vanity routing entirely — on the next page load after login.
 *   - access_token / user / tenant_id / login_time / session_expires_at /
 *     auth_type — the
 *     auth-bearing keys themselves; the logout flow rewrites them
 *     immediately, so wiping them here would just churn.
 *   - remembered_email / remembered_session — "Remember me". Owned by
 *     the login, logout and refresh flows, which set and clear them
 *     deliberately; a generic cache wipe must not sign anyone out.
 *   - design-system-floating-preview — super-admin debug UI state.
 */
const PRESERVE_EXACT = new Set<string>([
  'language',
  'theme',
  'access_token',
  'user',
  'tenant_id',
  'login_time',
  'session_expires_at',
  'auth_type',
  'remembered_email',
  'remembered_session',
  'design-system-floating-preview',
]);
const PRESERVE_PREFIXES: string[] = [
  'brand_overrides_v5:',
  'brand_page_title:',
  'vanity_host_slug:',
];

function shouldPreserve(key: string): boolean {
  if (PRESERVE_EXACT.has(key)) return true;
  for (const p of PRESERVE_PREFIXES) {
    if (key.startsWith(p)) return true;
  }
  return false;
}

/**
 * Wipe every user-scoped data cache from BOTH sessionStorage and
 * localStorage. Used on logout (clear the leaving user's traces) and
 * on login (clean slate for the new user — defense in depth in case
 * a path skipped logout). The allowlist above defines what survives.
 */
function clearUserDataCaches(): void {
  if (typeof window === 'undefined') return;
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && !shouldPreserve(k)) toRemove.push(k);
      }
      toRemove.forEach((k) => store.removeItem(k));
    } catch { /* ignore */ }
  }
}

// Each `useAuth()` call has its own React state, so when one component
// (e.g. LoginPage) calls authService.login() directly, sibling/ancestor
// useAuth instances (e.g. App.tsx) don't notice. We notify them via a
// custom event whenever auth-state-bearing localStorage keys change.
export const AUTH_CHANGE_EVENT = 'auth-state-changed';
function emitAuthChange(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  } catch {
    // older browsers don't allow `new Event()` — fall back
    const evt = document.createEvent('Event');
    evt.initEvent(AUTH_CHANGE_EVENT, true, true);
    window.dispatchEvent(evt);
  }
}

/** Whether this host is a tenant's vanity domain (the domain IS the tenant).
 *  The cache key is read inline: importing vanityHost would close an import
 *  cycle (vanityHost → tenantResolver → api → this file). */
function onVanityHost(): boolean {
  try {
    return !!window.localStorage.getItem('vanity_host_slug:' + window.location.host);
  } catch {
    return false; // localStorage unavailable — judge by the path
  }
}

/** The tenant slug the URL names, or '' when it names none. */
function urlTenantSlug(): string {
  const slug = (window.location.pathname.match(/^\/([^/]+)(\/|$)/)?.[1] || '').toLowerCase();
  return slug && !isReservedSlug(slug) ? slug : '';
}

/**
 * Whether the page's URL is the given login surface's, or names no tenant at
 * all. On a vanity host the domain IS the tenant, and localStorage is already
 * per-host, so anything remembered there belongs to it.
 */
function urlBelongsToLoginSlug(loginSlug: string): boolean {
  if (typeof window === 'undefined') return false;
  if (onVanityHost()) return true;
  const urlSlug = urlTenantSlug();
  return !urlSlug || urlSlug === loginSlug;
}

class AuthService {
  /** How long a verification answer stays warm. Short on purpose: long enough
   *  to collapse one page load's worth of duplicate calls, short enough that a
   *  server-side revocation is still noticed quickly. */
  private static readonly VERIFY_TTL_MS = 5000;
  private static _verifyCache: { token: string; result: boolean; at: number } | null = null;
  private static _verifyInFlight: { token: string; promise: Promise<boolean> } | null = null;

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!token && !!user;
  }

  /**
   * Get stored JWT token (per-tab session)
   */
  getToken(): string | null {
    return AUTH_STORE.getItem(AUTH_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get stored user data (per-tab session)
   */
  getUser(): User | null {
    try {
      const userStr = AUTH_STORE.getItem(AUTH_KEYS.USER);
      if (!userStr) return null;

      const user = JSON.parse(userStr);
      return user as User;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  /**
   * Get tenant ID from per-tab session or user object
   */
  getTenantId(): string | null {
    let tenantId = AUTH_STORE.getItem(AUTH_KEYS.TENANT_ID);

    if (!tenantId) {
      const user = this.getUser();
      if (user?.tenant_id) {
        tenantId = user.tenant_id;
        AUTH_STORE.setItem(AUTH_KEYS.TENANT_ID, tenantId);
      }
    }

    return tenantId;
  }

  /**
   * Check if user has a specific role
   * Role hierarchy: super_admin > group_admin > admin > user
   */
  hasRole(requiredRole: 'super_admin' | 'group_admin' | 'admin' | 'user'): boolean {
    const user = this.getUser();
    if (!user) return false;

    const userRole = user.role;

    // Role hierarchy map (higher number = more permissions)
    const roleHierarchy: Record<string, number> = {
      'super_admin': 4,
      'group_admin': 3,
      'admin': 2,
      'user': 1
    };

    const userRoleLevel = roleHierarchy[userRole] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    // User has access if their role level is >= required role level
    return userRoleLevel >= requiredRoleLevel;
  }

  /**
   * Check if user is super admin
   */
  isSuperAdmin(): boolean {
    return this.hasRole('super_admin');
  }

  /**
   * Check if user is admin (tenant admin or higher)
   */
  isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin' || user?.role === 'group_admin' || user?.role === 'super_admin';
  }

  /**
   * Check if user is group admin (or higher)
   */
  isGroupAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'group_admin' || user?.role === 'super_admin';
  }

  /**
   * Login with email and password.
   *
   * Returns one of two shapes:
   *   - A `LoginResponse` on success (token + user object).
   *   - A `{ pick_tenant: true, tenants: [...] }` payload when the
   *     URL is a company with `multi_user_login_allowed=true` AND the
   *     email matches more than one sub-tenant account. Caller should
   *     render a picker and re-call this method with
   *     `targetUserTenantId` set to the chosen tenant_id.
   *
   * Throws on hard errors (wrong creds, network failure).
   */
  async login(
    email: string,
    password: string,
    tenant: string = 'test',
    companyTenantCode?: string,
    targetUserTenantId?: string,
    rememberMe: boolean = false,
  ): Promise<LoginResponse | { pick_tenant: true; tenants: TenantPickerOption[] }> {
    try {
      console.log('[AUTH] Attempting login for:', email);

      const loginUrl = `${configService.getApiBaseUrl()}/auth/login`;

      const payload: LoginRequest & { tenant?: string; company_tenant_code?: string; target_user_tenant_id?: string; remember_me?: boolean } = {
        email,
        password,
        tenant,
        remember_me: rememberMe,
        ...(companyTenantCode ? { company_tenant_code: companyTenantCode } : {}),
        ...(targetUserTenantId ? { target_user_tenant_id: targetUserTenantId } : {}),
      };

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('[OK] Login successful');
        this.saveAuthData(data);
        this.applyRememberChoice(email, rememberMe, data);
        return data;
      }
      // Multi-user picker response — not an error, the caller has to
      // pick a tenant and call this method again with the choice.
      if (response.ok && data.action === 'pick_tenant' && Array.isArray(data.tenants)) {
        console.log('[AUTH] Multi-tenant choice required:', data.tenants.length, 'options');
        return { pick_tenant: true, tenants: data.tenants as TenantPickerOption[] };
      }
      console.log('[ERROR] Login failed:', data.error || data.message);
      throw new Error(data.error || data.message || 'Login failed');
    } catch (error) {
      console.error('[CRITICAL] Login error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error during login');
    }
  }

  /**
   * Save authentication data after successful login
   */
  private saveAuthData(authResponse: LoginResponse): void {
    const { user, access_token } = authResponse;

    // Defense in depth: before writing a DIFFERENT user's auth, wipe
    // any user-scoped data caches a previous user may have left in
    // sessionStorage. Without this, logging in as a different user
    // in the same tab would briefly paint the previous user's
    // cached list data (bots, conversations, skills, …) before the
    // network refresh replaced it. A refreshed session of the same
    // person keeps them — they are that person's own data.
    if (this.getUser()?.user_id !== user.user_id) {
      clearUserDataCaches();
    }

    // Per-tab session storage so a second tab can hold a different
    // tenant's session without overwriting this one.
    AUTH_STORE.setItem(AUTH_KEYS.ACCESS_TOKEN, access_token);
    AUTH_STORE.setItem(AUTH_KEYS.USER, JSON.stringify(user));
    AUTH_STORE.setItem(AUTH_KEYS.TENANT_ID, user.tenant_id);
    AUTH_STORE.setItem(AUTH_KEYS.LOGIN_TIME, new Date().toISOString());
    const lifetimeSeconds = authResponse.expires_in ?? SESSION_HOURS * 3600;
    AUTH_STORE.setItem(AUTH_KEYS.EXPIRES_AT, new Date(Date.now() + lifetimeSeconds * 1000).toISOString());
    AUTH_STORE.setItem(AUTH_KEYS.AUTH_TYPE, 'jwt');

    // The tenant's defaults go to localStorage, not the per-tab session: they
    // describe the browser's starting point for theme and language, which are
    // themselves localStorage-scoped. Putting them in sessionStorage would
    // mean a second tab started from the platform default instead.
    this.setTenantDefaults(user.default_user_settings);

    console.log('[OK] Auth data saved for user:', user.email);
    console.log('[AUTH] JWT token saved (length:', access_token.length, ')');

    // Notify other useAuth() instances IN THIS TAB so they re-sync.
    emitAuthChange();
  }

  /**
   * The person logs out. Ends the session on the SERVER as well as in this
   * tab: the access token is revoked, and when this browser remembers this
   * person, the remembered sign-in is revoked too (which ends it in every other
   * tab of the browser). The remembered email stays — "Not you?" clears it.
   *
   * Resolves once the server has confirmed (or LOGOUT_WAIT_MS passed) and the
   * tab's session is cleared — navigate only after that, or the navigation
   * cancels the request that ends the session.
   */
  async logout(): Promise<void> {
    console.log('[LOCK] Logging out user');
    const token = this.getToken();
    const remembered = this.ownsRememberedSession() ? this.getRememberedSession() : null;
    if (remembered) {
      localStorage.removeItem(AUTH_KEYS.REMEMBERED_SESSION);
    }
    if (token || remembered) {
      await this.postLogout(token, remembered?.refresh_token ?? null);
    }
    this.endTabSession();
  }

  /**
   * End THIS TAB's session only — nothing on the server, nothing remembered.
   * For a session that is already over (expired, refused) or that this tab
   * must drop without signing the person out elsewhere (a jump to another
   * tenant's URL).
   */
  endTabSession(): void {

    // Wipe user-scoped data caches (page lists, pricing, etc.) so
    // logging in as a different user in the same tab can never
    // surface the previous user's data.
    clearUserDataCaches();

    // Clear all auth-related per-tab keys.
    const authKeys: string[] = [
      AUTH_KEYS.ACCESS_TOKEN,
      AUTH_KEYS.USER,
      AUTH_KEYS.TENANT_ID,
      AUTH_KEYS.LOGIN_TIME,
      AUTH_KEYS.EXPIRES_AT,
      AUTH_KEYS.AUTH_TYPE,
    ];
    authKeys.forEach((key) => AUTH_STORE.removeItem(key));

    // Clear any legacy items that might still be in localStorage from
    // before the per-tab-session migration.
    try {
      authKeys.forEach((key) => localStorage.removeItem(key));
      localStorage.removeItem('refresh_token');
    } catch { /* ignore */ }

    // Notify all useAuth() instances in this tab so they clear React state.
    emitAuthChange();

    // Navigation is handled by React Router via useAuth state change
    // The ProtectedRoute component will redirect to /login when isAuthenticated becomes false
  }

  /**
   * Verify token validity with backend
   * Returns true if token is valid, false if expired/invalid
   *
   * SINGLE-FLIGHT + short TTL. `useAuth()` is a plain hook rather than a
   * context, so every component that calls it runs its own init effect and
   * its own verification on mount. Opening one conversation mounts enough of
   * them to fire this ~48 times — 48 round trips, each doing real work
   * server-side, all asking the identical question about the identical token.
   *
   * Concurrent callers now share one in-flight request, and the answer is
   * reused for a few seconds afterwards. Behaviour is unchanged (the same
   * verification still happens, the same user refresh still propagates); only
   * the redundancy goes away. The TTL is deliberately short so a token
   * revoked server-side is still noticed promptly.
   */
  async verifyToken(force = false): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    // `force` exists because verifyToken has a SECOND job: it refreshes the
    // cached user (features, brand overrides) and notifies readers. Callers
    // that toggled something server-side and need the new state — rather than
    // callers merely asking "is this token still good?" — must not be served a
    // cached answer, or the refresh silently never happens.
    if (force) {
      AuthService._verifyCache = null;
      AuthService._verifyInFlight = null;
    }

    const now = Date.now();
    // A result for THIS token that's still warm — reuse it.
    if (
      !force &&
      AuthService._verifyCache &&
      AuthService._verifyCache.token === token &&
      now - AuthService._verifyCache.at < AuthService.VERIFY_TTL_MS
    ) {
      return AuthService._verifyCache.result;
    }
    // A request for THIS token already in the air — wait on it rather than
    // starting a second one.
    if (!force && AuthService._verifyInFlight && AuthService._verifyInFlight.token === token) {
      return AuthService._verifyInFlight.promise;
    }

    const promise = this._verifyTokenUncached(token)
      .then(result => {
        AuthService._verifyCache = { token, result, at: Date.now() };
        return result;
      })
      .finally(() => {
        if (AuthService._verifyInFlight?.token === token) AuthService._verifyInFlight = null;
      });
    AuthService._verifyInFlight = { token, promise };
    return promise;
  }

  /** The actual round trip. Only ever called through `verifyToken`. */
  private async _verifyTokenUncached(token: string): Promise<boolean> {
    try {
      const response = await fetch(`${configService.getApiBaseUrl()}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          // Update stored user data in case it changed on the server, then
          // notify useAuth() instances so they pick up the refreshed user
          // (notably user.brand_overrides / user.features which drive
          // App.tsx's brand-resolution + RequireFeature gating).
          const prev = AUTH_STORE.getItem(AUTH_KEYS.USER);
          const next = JSON.stringify(data.user);
          if (prev !== next) {
            AUTH_STORE.setItem(AUTH_KEYS.USER, next);
            emitAuthChange();
          }
          // Refresh the tenant defaults on every verify, not only on the diff
          // above: a super admin changing a tenant's default language should
          // reach that tenant's users on their next token check, without
          // waiting for some other field of the user object to move.
          this.setTenantDefaults(data.user.default_user_settings);
          return true;
        }
      }

      // Token invalid
      return false;
    } catch (error) {
      console.error('[AUTH] Token verification failed:', error);
      // On network error, fall back to the token's own expiry
      return this.getSessionExpiresAt() > Date.now();
    }
  }

  /**
   * When this tab's access token expires (epoch ms), from the server's
   * `expires_in`. A session saved before that was recorded counts 48 hours
   * from its login, as it always did.
   */
  getSessionExpiresAt(): number {
    const expiresAt = AUTH_STORE.getItem(AUTH_KEYS.EXPIRES_AT);
    if (expiresAt) return Date.parse(expiresAt);
    const loginTime = AUTH_STORE.getItem(AUTH_KEYS.LOGIN_TIME);
    return loginTime ? Date.parse(loginTime) + SESSION_HOURS * 3600 * 1000 : 0;
  }

  /**
   * Hard navigation to `user`'s OWN branded login (`/<their-tenant>/login`) —
   * never the tenant the page happens to show. Without a user, the URL's
   * tenant, then the platform default. Callers pass the user they captured
   * BEFORE clearing the session.
   */
  redirectToOwnLogin(user: User | null): void {
    if (typeof window === 'undefined') return;
    if (window.location.pathname.endsWith('/login')) return;
    // On a vanity host the tenant slug is implied by the domain and must
    // never appear in the URL. This is a HARD navigation, so it bypasses
    // the slug-hiding history — write the slug-less path ourselves.
    if (onVanityHost()) { window.location.replace('/login'); return; }
    const ownSlug = (user?.company_tenant_code || user?.tenant_code || '').toLowerCase();
    const slug = ownSlug || urlTenantSlug();
    window.location.replace(slug ? `/${slug}/login` : DEFAULT_LOGIN_PATH);
  }

  // ── "Remember me" ─────────────────────────────────────────────────────

  /** The email the login form starts with, or '' when none is remembered. */
  getRememberedEmail(): string {
    try {
      return localStorage.getItem(AUTH_KEYS.REMEMBERED_EMAIL) || '';
    } catch {
      return '';
    }
  }

  /**
   * "Not you?" — someone else is at this browser. Forget the email AND end
   * the remembered sign-in on the server, so it cannot be picked up again.
   */
  forgetRememberedUser(): void {
    const remembered = this.getRememberedSession();
    if (remembered) this.postLogout(null, remembered.refresh_token);
    localStorage.removeItem(AUTH_KEYS.REMEMBERED_EMAIL);
    localStorage.removeItem(AUTH_KEYS.REMEMBERED_SESSION);
  }

  /** Whether this browser remembers the person signed in to this tab. */
  ownsRememberedSession(): boolean {
    const remembered = this.getRememberedSession();
    return !!remembered && remembered.user_id === this.getUser()?.user_id;
  }

  /**
   * A tab with no session of its own picks up the browser's remembered
   * sign-in — when the URL is that sign-in's own login surface (or carries no
   * tenant at all). On another tenant's URL the person meant that tenant, so
   * its login form shows instead. Resolves true when the tab is signed in.
   */
  async restoreRememberedSession(): Promise<boolean> {
    const remembered = this.getRememberedSession();
    if (!remembered || !urlBelongsToLoginSlug(remembered.login_slug)) return false;
    return this.refreshRemembered(null);
  }

  /**
   * This tab's access token is over (expired, or answered 401): renew it from
   * the remembered sign-in — only if that sign-in is THIS tab's person; a tab
   * signed in to another tenant must never be switched to the remembered one.
   */
  async refreshTabSession(): Promise<boolean> {
    const userId = this.getUser()?.user_id;
    if (!userId || !this.ownsRememberedSession()) return false;
    return this.refreshRemembered(userId);
  }

  private static _refreshInFlight: Promise<boolean> | null = null;

  /** Single-flight within the tab; serialised across tabs by a Web Lock. */
  private refreshRemembered(expectedUserId: string | null): Promise<boolean> {
    if (AuthService._refreshInFlight) return AuthService._refreshInFlight;
    const run = () => this.exchangeRefreshToken(expectedUserId);
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    // `.then` flattens: lib.dom types request()'s result as Promise<Promise<T>>.
    const exchanged: Promise<boolean> = locks
      ? locks.request(REFRESH_LOCK, run).then((result) => result)
      : run();
    const promise = exchanged.finally(() => { AuthService._refreshInFlight = null; });
    AuthService._refreshInFlight = promise;
    return promise;
  }

  private async exchangeRefreshToken(expectedUserId: string | null): Promise<boolean> {
    // Read INSIDE the lock: another tab may have just exchanged the token
    // this tab saw a moment ago, leaving its successor here.
    const remembered = this.getRememberedSession();
    if (!remembered) return false;
    if (expectedUserId && remembered.user_id !== expectedUserId) return false;

    let response: Response;
    try {
      response = await fetch(`${configService.getApiBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: remembered.refresh_token }),
      });
    } catch (error) {
      // Offline: keep the remembered sign-in for the next attempt.
      console.error('[AUTH] Remembered sign-in refresh failed:', error);
      return false;
    }
    if (response.status === 401) {
      // The server ended it (expired, revoked, reused, user deactivated).
      // Forget it — unless another tab replaced it in the meantime.
      if (this.getRememberedSession()?.refresh_token === remembered.refresh_token) {
        localStorage.removeItem(AUTH_KEYS.REMEMBERED_SESSION);
      }
      return false;
    }
    if (!response.ok) return false; // rate limit / server error: try again later

    const data: LoginResponse = await response.json();
    if (!data.refresh_token) return false;
    this.storeRememberedSession(data.refresh_token, data.refresh_expires_at, data.user);
    this.saveAuthData(data);
    return true;
  }

  /**
   * After a password sign-in: a ticked box remembers the email and the new
   * sign-in; an unticked one forgets both. Either way a sign-in this browser
   * remembered before is revoked on the server — it is being replaced, or the
   * person at this browser has just chosen not to be remembered here.
   */
  private applyRememberChoice(email: string, remember: boolean, data: LoginResponse): void {
    const previous = this.getRememberedSession();
    if (previous && previous.refresh_token !== data.refresh_token) {
      this.postLogout(null, previous.refresh_token);
    }
    if (remember && data.refresh_token) {
      localStorage.setItem(AUTH_KEYS.REMEMBERED_EMAIL, email.trim());
      this.storeRememberedSession(data.refresh_token, data.refresh_expires_at, data.user);
    } else {
      localStorage.removeItem(AUTH_KEYS.REMEMBERED_EMAIL);
      localStorage.removeItem(AUTH_KEYS.REMEMBERED_SESSION);
    }
  }

  private storeRememberedSession(refreshToken: string, expiresAt: string | undefined, user: User): void {
    const session: RememberedSession = {
      refresh_token: refreshToken,
      user_id: user.user_id,
      login_slug: (user.company_tenant_code || user.tenant_code || '').toLowerCase(),
      expires_at: expiresAt || '',
    };
    localStorage.setItem(AUTH_KEYS.REMEMBERED_SESSION, JSON.stringify(session));
  }

  private getRememberedSession(): RememberedSession | null {
    try {
      const raw = localStorage.getItem(AUTH_KEYS.REMEMBERED_SESSION);
      if (!raw) return null;
      const session = JSON.parse(raw) as RememberedSession;
      if (!session.refresh_token || !session.user_id) return null;
      if (session.expires_at && Date.parse(session.expires_at) <= Date.now()) {
        localStorage.removeItem(AUTH_KEYS.REMEMBERED_SESSION);
        return null;
      }
      return session;
    } catch {
      return null;
    }
  }

  /** End on the server. Waits for the answer, but at most LOGOUT_WAIT_MS —
   *  a slow or unreachable server must never keep anyone signed in locally.
   *  keepalive lets the request finish even if the page goes away first. */
  private postLogout(accessToken: string | null, refreshToken: string | null): Promise<void> {
    const request = fetch(`${configService.getApiBaseUrl()}/auth/logout`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
    })
      // Read the body too: until it is consumed the request is still open, and
      // the navigation that follows would cancel it.
      .then(async (response) => {
        await response.text();
        if (!response.ok) console.error('[AUTH] Server logout failed:', response.status);
      })
      .catch((error) => console.error('[AUTH] Server logout failed:', error));
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, LOGOUT_WAIT_MS));
    return Promise.race([request, timeout]);
  }

  /**
   * The tenant's default user settings, as sent on every auth response.
   * Empty until the first login/verify of this browser.
   */
  getTenantDefaults(): Partial<TenantDefaultUserSettings> {
    try {
      const raw = localStorage.getItem(AUTH_KEYS.TENANT_DEFAULTS);
      return raw ? (JSON.parse(raw) as Partial<TenantDefaultUserSettings>) : {};
    } catch {
      return {};
    }
  }

  /**
   * Remember what this tenant hands a user who has not chosen. Called from
   * every auth response, so changing a tenant default reaches returning users
   * too — it still only applies where the person has no stored preference.
   */
  setTenantDefaults(defaults: Partial<TenantDefaultUserSettings> | null | undefined): void {
    if (!defaults) return;
    localStorage.setItem(AUTH_KEYS.TENANT_DEFAULTS, JSON.stringify(defaults));
  }

  /**
   * Get user's preferred language.
   *
   * Order: the person's own choice → their tenant's default → English. A
   * tenant that works in Hebrew should not hand every new employee an English
   * screen, but it must never overrule someone who switched deliberately.
   */
  getLanguage(): 'en' | 'he' {
    const lang = localStorage.getItem(AUTH_KEYS.LANGUAGE);
    if (lang === 'he' || lang === 'en') return lang;
    const fallback = this.getTenantDefaults().language;
    return fallback === 'he' ? 'he' : 'en';
  }

  /**
   * Set user's preferred language
   */
  setLanguage(language: 'en' | 'he'): void {
    localStorage.setItem(AUTH_KEYS.LANGUAGE, language);
  }

  /**
   * Get user's preferred theme.
   *
   * Same order as the language: the person's own choice wins, then their
   * tenant's default, then the platform's historical light.
   */
  getTheme(): 'light' | 'dark' {
    const theme = localStorage.getItem(AUTH_KEYS.THEME);
    if (theme === 'light' || theme === 'dark') return theme;
    return this.getTenantDefaults().theme === 'dark' ? 'dark' : 'light';
  }

  /**
   * Set user's preferred theme
   */
  setTheme(theme: 'light' | 'dark'): void {
    localStorage.setItem(AUTH_KEYS.THEME, theme);
  }

  /**
   * Change current user's password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${configService.getApiBaseUrl()}/auth/me/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
        // The change revokes every remembered sign-in; ask for a new one for
        // this browser if it remembered this person.
        remember_me: this.ownsRememberedSession(),
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to change password');
    }

    // The backend invalidates every outstanding session on password change
    // and mints a fresh token for THIS tab — swap it in so the current
    // session survives while stolen/other sessions die.
    if (data.access_token) {
      AUTH_STORE.setItem(AUTH_KEYS.ACCESS_TOKEN, data.access_token);
    }
    const user = this.getUser();
    if (data.refresh_token && user) {
      this.storeRememberedSession(data.refresh_token, data.refresh_expires_at, user);
    }
  }

  /**
   * Self-service (Settings): email the current user a password-reset link.
   */
  async sendMyPasswordResetEmail(): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${configService.getApiBaseUrl()}/auth/me/password-reset-email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      // A 5xx body is generic by design (request_guards): the page words it.
      throw new Error(response.status >= 500 ? '' : (data.error || ''));
    }
  }

  /**
   * Anonymous "forgot password" request (login page). The backend always
   * answers with a generic success — account existence is never revealed.
   */
  async requestPasswordReset(email: string, companyTenantCode?: string): Promise<void> {
    const response = await fetch(`${configService.getApiBaseUrl()}/public/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        company_tenant_code: companyTenantCode ?? null,
      }),
    });

    if (!response.ok) {
      // Only rate-limit (429) / validation (400) can differ from the generic 200.
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to request a password reset');
    }
  }

  /**
   * Public reset page: check a token before showing the set-password form.
   */
  async validatePasswordToken(token: string): Promise<{
    valid: boolean;
    purpose?: 'reset' | 'invite';
    email_masked?: string;
    tenant_code?: string;
  }> {
    const response = await fetch(`${configService.getApiBaseUrl()}/public/password-token/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      return { valid: false };
    }
    return response.json();
  }

  /**
   * Public reset page: consume the token and set the new password.
   * Returns the tenant_code for the post-success login redirect.
   */
  async resetPasswordWithToken(token: string, newPassword: string): Promise<{ tenant_code: string }> {
    const response = await fetch(`${configService.getApiBaseUrl()}/public/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'This link is invalid or has expired.');
    }
    return { tenant_code: data.tenant_code };
  }
}

// Create and export singleton instance
const authService = new AuthService();

export default authService;
